import { t } from "i18next";

import { DirectionsCar, TwoWheeler } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { distinctFillColor } from "@/src/theme/helpers";

import { VerticalSource } from "@/src/types/types";

interface VerticalCheckItemProps {
  auctionId: number;
  regNo: string;
  yTunnus: string;
  vertical: VerticalSource;
  businessName: string;
  listingDate: string;
  listingStatus: string;
  adLink: string;
  reviewStatus: "NEW" | "REVIEWED";
  reviewedAt?: string;
  onMarkReviewed: (auctionId: number) => void;
  onViewAuction?: (auctionId: number) => void;
  isLoadingAuction?: boolean;
  isLoading?: boolean;
}

const VerticalCheckItem = ({
  auctionId,
  regNo,
  yTunnus,
  vertical,
  businessName,
  listingDate,
  listingStatus,
  adLink,
  reviewStatus,
  reviewedAt,
  onMarkReviewed,
  onViewAuction,
  isLoadingAuction = false,
  isLoading = false
}: VerticalCheckItemProps) => {
  const theme = useTheme();

  const handleMarkReviewed = () => {
    if (!isLoading && reviewStatus === "NEW") {
      onMarkReviewed(auctionId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fi-FI", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const getVerticalIcon = () => {
    return vertical === VerticalSource.Nettiauto ? (
      <DirectionsCar />
    ) : (
      <TwoWheeler />
    );
  };

  const getStatusChip = () => {
    if (reviewStatus === "NEW") {
      return (
        <Chip
          label={t("admin.verticalChecks.labels.needsReview")}
          color="warning"
          size="small"
          sx={{ fontWeight: theme.typography.fontWeightSemibold }}
        />
      );
    }

    const reviewedDate = reviewedAt
      ? formatDate(reviewedAt)
      : t("admin.verticalChecks.labels.unknown");
    return (
      <Chip
        label={`${t("admin.verticalChecks.labels.reviewed")} ${reviewedDate}`}
        sx={{
          backgroundColor: theme.palette.semantic.success.enabled50p,
          color: theme.palette.semantic.success.alertText,
          fontWeight: theme.typography.fontWeightSemibold
        }}
        size="small"
      />
    );
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        opacity: reviewStatus === "REVIEWED" ? 0.6 : 1,
        backgroundColor:
          reviewStatus === "REVIEWED"
            ? distinctFillColor(theme)
            : theme.palette.background.paper,
        transition: "opacity 0.2s ease",
        "&:hover": {
          opacity: reviewStatus === "REVIEWED" ? 0.8 : 1,
          boxShadow: 2
        }
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {getVerticalIcon()}
            </Box>
            <Typography
              variant="h6"
              component="span"
              sx={{
                fontWeight: theme.typography.fontWeightSemibold,
                margin: 1
              }}
            >
              {regNo}
            </Typography>
            <Chip
              label={vertical.toUpperCase()}
              size="small"
              variant="outlined"
              sx={{
                fontSize: (theme) => theme.typography.caption.fontSize,
                height: 20
              }}
            />
          </Box>
          {getStatusChip()}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            mb: 2
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t("admin.verticalChecks.item.auctionId")}:</strong>{" "}
              {onViewAuction ? (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5
                  }}
                >
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => onViewAuction(auctionId)}
                    disabled={isLoadingAuction}
                    sx={{
                      fontWeight: theme.typography.fontWeightSemibold,
                      textDecoration: "underline",
                      cursor: isLoadingAuction ? "default" : "pointer",
                      opacity: isLoadingAuction ? 0.6 : 1,
                      "&:hover": {
                        textDecoration: "underline",
                        opacity: isLoadingAuction ? 0.6 : 0.8
                      }
                    }}
                  >
                    {auctionId}
                  </Link>
                  {isLoadingAuction && (
                    <CircularProgress size={12} sx={{ ml: 0.5 }} />
                  )}
                </Box>
              ) : (
                auctionId
              )}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t("admin.verticalChecks.item.businessName")}:</strong>{" "}
              {businessName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t("admin.verticalChecks.item.yTunnus")}:</strong>{" "}
              {yTunnus}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t("admin.verticalChecks.item.listingDate")}:</strong>{" "}
              {formatDate(listingDate)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>{t("admin.verticalChecks.item.listingStatus")}:</strong>{" "}
              {listingStatus}
            </Typography>
            <Link
              href={adLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ fontWeight: theme.typography.fontWeightMedium }}
            >
              {t("admin.verticalChecks.labels.viewListing")}
            </Link>
          </Box>
        </Box>
      </CardContent>

      {reviewStatus === "NEW" && (
        <CardActions sx={{ pt: 0 }}>
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleMarkReviewed}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
            >
              {isLoading
                ? t("admin.verticalChecks.messages.markingAsReviewed")
                : t("admin.verticalChecks.item.actions.markReviewed")}
            </Button>
          </Box>
        </CardActions>
      )}
    </Card>
  );
};

export default VerticalCheckItem;
